import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import Icon from './Icons';
import { photoUrl } from './photoUtils';
import { useGrowingMethods, GrowingMethodSubstrateSelect, formatSubstrate } from './GrowingMethodPicker';
import ErrorScreen from './ErrorScreen';
import { useConnectivity } from './hooks';
import { fieldOptionList, useFieldOptions } from './fieldOptions';
import { formatStatus as formatPlantStatus, statusStyle, useStatusMeta } from './statuses';
import { useGuidance } from './GuidanceContext';
import GuidanceSheet from './GuidanceSheet';
import { ROUNDS_GUIDANCE_TITLE, RoundsGuidanceBody } from './guidanceContent';

// Batch size before prompting "keep going?"
const BATCH_SIZE = 10;

// Days after a check-in before a plant re-enters the queue
const SUPPRESSION_DAYS = 3;
const STORAGE_KEY = 'rounds_suppressed';

// Observation types for the "+ Observation" flow
const OBS_TYPES = [
  { value: 'health_check',     label: 'Health Check' },
  { value: 'watering',         label: 'Watering' },
  { value: 'fertilize',        label: 'Fertilize' },
  { value: 'pruning',          label: 'Pruning' },
  { value: 'repot',            label: 'Repot' },
  { value: 'treatment',        label: 'Treatment' },
  { value: 'staking_training', label: 'Staking / Training' },
  { value: 'general_note',     label: 'General Note' },
];

const TASK_TYPES = [
  { value: 'health_check',     label: 'Health Check' },
  { value: 'watering',         label: 'Watering' },
  { value: 'fertilize',        label: 'Fertilize' },
  { value: 'repot',            label: 'Repot' },
  { value: 'treatment',        label: 'Treatment' },
  { value: 'pruning',          label: 'Pruning' },
  { value: 'staking_training', label: 'Staking / Training' },
  { value: 'substrate_change', label: 'Substrate Change' },
  { value: 'measurement',      label: 'Measurement' },
  { value: 'general_note',     label: 'General Note' },
];

const THEME_OPTIONS = [
  { key: 'none',              label: 'Most Overdue',       description: "Plants that haven't been tended to in the longest time", icon: 'clock',          hasSubFilter: false },
  { key: 'random',            label: 'Surprise Me',        description: 'Smart shuffle weighted toward your most neglected plants', icon: 'dice5',         hasSubFilter: false },
  { key: 'growing_method',    label: 'By Growing Method',  description: 'Check all soil, semi-hydro, or water plants together',   icon: 'stone',          hasSubFilter: true  },
  { key: 'location',          label: 'By Location',        description: 'Work through one room at a time',                        icon: 'mapPin',         hasSubFilter: true  },
  { key: 'status',            label: 'By Plant Status',    description: 'Care for plants by lifecycle stage: collection, propagation stock, available, loaned.', icon: 'clipboardCheck', hasSubFilter: true },
  { key: 'tag',               label: 'By Tag',             description: 'Focus on a custom group like Rare or Pest Watch',        icon: 'tag',            hasSubFilter: true  },
  { key: 'propagation_tray',  label: 'Propagation Bay',    description: 'Check active cuttings, corms, offsets, and other pre-plant items.', icon: 'sprout', hasSubFilter: false },
];

const PROP_STATUS_LABELS = {
  active: 'Active', sprouting: 'Sprouting', rooting: 'Rooting', rooted: 'Rooted',
};
const PROP_STATUS_ORDER = ['active', 'sprouting', 'rooting', 'rooted'];
const PROP_METHOD_LABELS = {
  cutting: 'Cutting', wet_stick: 'Wet Stick', division: 'Division',
  offset_pup: 'Offset / Pup', corm: 'Corm', air_layer: 'Air Layer',
  tissue_culture: 'TC', runner_stolon: 'Stolon', rhizome_division: 'Rhizome Div.',
  bulbil: 'Bulbil', graft: 'Graft', other_clone: 'Clone',
};

// Action types for the color counter
const ACTION_COLORS = {
  looksGood:   { bg: 'var(--green)',   label: 'Looks Good'   },
  observation: { bg: '#3b82f6',        label: 'Observation'  },
  measurement: { bg: '#f59e0b',        label: 'Measurement'  },
  skip:        { bg: 'var(--border)',  label: 'Skipped'      },
};

// ─── Suppression helpers ────────────────────────────────────────────────────

function getSuppressed() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
  catch { return {}; }
}

function suppressPlant(plantId) {
  const map = getSuppressed();
  map[plantId] = new Date().toISOString();
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
}

function isSuppressed(plantId) {
  const map = getSuppressed();
  const ts = map[plantId];
  if (!ts) return false;
  const elapsed = (Date.now() - new Date(ts).getTime()) / (1000 * 60 * 60 * 24);
  return elapsed < SUPPRESSION_DAYS;
}

// ─── Color counter bar ───────────────────────────────────────────────────────

function CounterBar({ actions, batchOffset, queueSize }) {
  // Adapt slot count to actual queue size — cap at BATCH_SIZE
  const slotCount = Math.min(queueSize, BATCH_SIZE);
  const slots = Array.from({ length: slotCount });
  return (
    <div className="round-counter-bar">
      {slots.map((_, i) => {
        const action = actions[i];
        const color = action ? ACTION_COLORS[action]?.bg : 'var(--border-light)';
        return (
          <div
            key={i}
            className={`round-counter-slot ${action ? 'round-counter-slot--filled' : ''}`}
            style={{ background: color }}
            title={action ? ACTION_COLORS[action]?.label : ''}
          />
        );
      })}
      {batchOffset > 0 && (
        <span className="round-counter-total">+{batchOffset} prev</span>
      )}
    </div>
  );
}

// ─── Photo button ────────────────────────────────────────────────────────────

function PhotoButton({ plantId, onUploaded }) {
  const [file,      setFile]      = useState(null);
  const [preview,   setPreview]   = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded,  setUploaded]  = useState(false);
  const [error,     setError]     = useState(null);
  const inputRef = useRef(null);

  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setUploaded(false);
    setError(null);
    e.target.value = '';
  };

  const handleUpload = useCallback((f) => {
    if (!f || uploading) return;
    setUploading(true);
    setError(null);
    const formData = new FormData();
    formData.append('file', f);
    fetch(`/plants/${plantId}/photos`, { method: 'POST', body: formData })
      .then(r => { if (!r.ok) throw new Error('Upload failed'); return r.json(); })
      .then(() => { setUploading(false); setUploaded(true); if (onUploaded) onUploaded(); })
      .catch(err => { setError(err.message); setUploading(false); });
  }, [plantId, uploading, onUploaded]);

  useEffect(() => {
    if (file && !uploaded) handleUpload(file);
  }, [file]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClear = () => { setFile(null); setPreview(null); setUploaded(false); setError(null); };

  if (preview) {
    return (
      <div className="round-photo-preview">
        <img src={preview} alt="Selected" className="round-photo-thumb" />
        <div className="round-photo-preview-info">
          {uploading && <span className="round-photo-status round-photo-status--uploading"><Icon name="clock" size={13} /> Uploading…</span>}
          {uploaded  && <span className="round-photo-status round-photo-status--done"><Icon name="check" size={13} /> Saved</span>}
          {error     && <span className="round-photo-status round-photo-status--error"><Icon name="x" size={13} /> {error}</span>}
          {!uploading && <button type="button" className="round-photo-clear" onClick={handleClear} aria-label="Remove" title="Remove"><Icon name="x" size={14} /></button>}
        </div>
      </div>
    );
  }

  return (
    <>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleChange} />
      <button type="button" className="round-photo-btn" onClick={() => inputRef.current?.click()} aria-label="Add photo">
        <Icon name="camera" size={15} /><span>Add photo</span>
      </button>
    </>
  );
}

// ─── Looks Good form ────────────────────────────────────────────────────────

function LooksGoodForm({ plantId, onSubmit, onCancel }) {
  const [notes, setNotes] = useState('');
  return (
    <div className="round-inline-form">
      <textarea className="health-textarea" placeholder="Any notes? (optional)" value={notes} onChange={e => setNotes(e.target.value)} rows={2} />
      <PhotoButton plantId={plantId} />
      <div className="round-inline-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => onSubmit(notes)}>Confirm →</button>
      </div>
    </div>
  );
}

// ─── Observation form ────────────────────────────────────────────────────────

function ObservationForm({ plantId, plantGrowingMethod, plantSubstrate, onSubmit, onCancel }) {
  const [types, setTypes] = useState(new Set(['health_check']));
  const [notes, setNotes] = useState('');
  const [repotMethod, setRepotMethod]       = useState('');
  const [repotSubstrate, setRepotSubstrate] = useState('');
  const [oldMethod, setOldMethod]           = useState('');
  const [oldSubstrate, setOldSubstrate]     = useState('');
  const [newMethod, setNewMethod]           = useState('');
  const [newSubstrate, setNewSubstrate]     = useState('');
  const growingMethods = useGrowingMethods();

  const toggle = (val) => {
    setTypes(prev => {
      const next = new Set(prev);
      if (next.has(val)) {
        next.delete(val);
      } else {
        next.add(val);
        if (val === 'substrate_change') {
          setOldMethod(plantGrowingMethod || '');
          setOldSubstrate(plantSubstrate || '');
        }
      }
      return next;
    });
  };

  const details = {};
  if (types.has('repot') && (repotMethod || repotSubstrate)) {
    details.repot = { new_growing_method: repotMethod || null, new_substrate: repotSubstrate || null };
  }
  if (types.has('substrate_change') && (oldMethod || oldSubstrate || newMethod || newSubstrate)) {
    details.substrate_change = {
      old_growing_method: oldMethod || null,
      old_substrate: oldSubstrate || null,
      new_growing_method: newMethod || null,
      new_substrate: newSubstrate || null,
    };
  }

  return (
    <div className="round-inline-form">
      <p className="health-field-label">Observation type <span className="health-field-label-note">— select all that apply</span></p>
      <div className="round-type-chips">
        {OBS_TYPES.map(t => (
          <button type="button" key={t.value} className={`health-chip ${types.has(t.value) ? 'health-chip--active' : ''}`} onClick={() => toggle(t.value)}>
            {t.label}
          </button>
        ))}
      </div>

      {types.has('repot') && (
        <div style={{ marginTop: '0.65rem' }}>
          <p className="health-field-label" style={{ marginBottom: '0.35rem' }}>Repot details</p>
          <GrowingMethodSubstrateSelect
            methods={growingMethods}
            methodValue={repotMethod}
            substrateValue={repotSubstrate}
            onMethodChange={v => { setRepotMethod(v); setRepotSubstrate(''); }}
            onSubstrateChange={setRepotSubstrate}
            methodLabel="New Growing Method"
            substrateLabel="New Substrate"
          />
        </div>
      )}

      {types.has('substrate_change') && (
        <div style={{ marginTop: '0.65rem' }}>
          <p className="health-field-label" style={{ marginBottom: '0.35rem' }}>Substrate change details</p>
          <GrowingMethodSubstrateSelect
            methods={growingMethods}
            methodValue={oldMethod}
            substrateValue={oldSubstrate}
            onMethodChange={v => { setOldMethod(v); setOldSubstrate(''); }}
            onSubstrateChange={setOldSubstrate}
            methodLabel="Old Growing Method"
            substrateLabel="Old Substrate"
          />
          <GrowingMethodSubstrateSelect
            methods={growingMethods}
            methodValue={newMethod}
            substrateValue={newSubstrate}
            onMethodChange={v => { setNewMethod(v); setNewSubstrate(''); }}
            onSubstrateChange={setNewSubstrate}
            methodLabel="New Growing Method"
            substrateLabel="New Substrate"
          />
        </div>
      )}

      <textarea className="health-textarea" placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ marginTop: '0.65rem' }} />
      <PhotoButton plantId={plantId} />
      <div className="round-inline-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" disabled={types.size === 0} onClick={() => onSubmit([...types], notes, details)}>
          Save {types.size > 1 ? `(${types.size})` : ''} →
        </button>
      </div>
    </div>
  );
}

// ─── Measurement form ────────────────────────────────────────────────────────

function MeasurementForm({ plantId, onSubmit, onCancel }) {
  const [height, setHeight] = useState('');
  const [leaf,   setLeaf]   = useState('');
  const [count,  setCount]  = useState('');
  const [notes,  setNotes]  = useState('');
  const hasAnyValue = height.trim() || leaf.trim() || count.trim();

  return (
    <div className="round-inline-form">
      <p className="health-field-label" style={{ marginBottom: '0.4rem' }}>Measurements <span className="health-field-label-note">— inches</span></p>
      <div className="round-measure-grid">
        <div className="round-measure-field">
          <label className="health-field-label">Height</label>
          <input type="number" inputMode="decimal" className="round-measure-input" placeholder="—" value={height} onChange={e => setHeight(e.target.value)} />
        </div>
        <div className="round-measure-field">
          <label className="health-field-label">Longest leaf</label>
          <input type="number" inputMode="decimal" className="round-measure-input" placeholder="—" value={leaf} onChange={e => setLeaf(e.target.value)} />
        </div>
        <div className="round-measure-field">
          <label className="health-field-label">Leaf count</label>
          <input type="number" inputMode="numeric" className="round-measure-input" placeholder="—" value={count} onChange={e => setCount(e.target.value)} />
        </div>
      </div>
      <textarea className="health-textarea" placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ marginTop: '0.65rem' }} />
      <PhotoButton plantId={plantId} />
      <div className="round-inline-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => onSubmit({ height, leaf, count, notes })} disabled={!hasAnyValue}>Save →</button>
      </div>
    </div>
  );
}

// ─── Schedule care form ──────────────────────────────────────────────────────

function formatLocalDate(date) {
  const copy = new Date(date);
  copy.setMinutes(copy.getMinutes() - copy.getTimezoneOffset());
  return copy.toISOString().split('T')[0];
}

function nextSaturday() {
  const date = new Date();
  const day = date.getDay();
  const daysUntilSaturday = (6 - day + 7) % 7 || 7;
  date.setDate(date.getDate() + daysUntilSaturday);
  return formatLocalDate(date);
}

function addDays(days) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return formatLocalDate(date);
}

function ScheduleCareForm({ plantId, onSubmit, onCancel }) {
  const [taskType, setTaskType] = useState('repot');
  const [date, setDate] = useState(nextSaturday());
  const [notes, setNotes] = useState('');

  return (
    <div className="round-inline-form">
      <p className="health-field-label" style={{ marginBottom: '0.4rem' }}>Schedule care</p>
      <div className="round-form-grid">
        <label className="round-form-field">
          <span className="health-field-label">Task</span>
          <select className="round-select" value={taskType} onChange={e => setTaskType(e.target.value)}>
            {TASK_TYPES.map(type => <option key={type.value} value={type.value}>{type.label}</option>)}
          </select>
        </label>
        <label className="round-form-field">
          <span className="health-field-label">Date</span>
          <input className="round-measure-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
        </label>
      </div>
      <div className="round-quick-date-row">
        <button type="button" className="round-quick-chip" onClick={() => setDate(addDays(1))}>Tomorrow</button>
        <button type="button" className="round-quick-chip" onClick={() => setDate(nextSaturday())}>Weekend</button>
        <button type="button" className="round-quick-chip" onClick={() => setDate(addDays(7))}>Next week</button>
      </div>
      <textarea className="health-textarea" placeholder="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} rows={2} style={{ marginTop: '0.65rem' }} />
      <div className="round-inline-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
        <button type="button" className="btn btn-primary btn-sm" onClick={() => onSubmit({ plant_id: plantId, task_type: taskType, starts_on: date, notes: notes || null, recurrence_type: 'once' })} disabled={!date}>Add Task</button>
      </div>
    </div>
  );
}

// ─── Location update form ────────────────────────────────────────────────────

function LocationUpdateForm({ plantId, onSubmit, onCancel }) {
  const [locations, setLocations] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch('/locations').then(r => { if (!r.ok) throw new Error('Failed to load locations'); return r.json(); }),
      fetch(`/plants/${plantId}`).then(r => { if (!r.ok) throw new Error('Failed to load plant'); return r.json(); }),
    ])
      .then(([locs, plant]) => {
        setLocations(locs);
        setSelected(new Set((plant.locations || []).map(loc => loc.id)));
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [plantId]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="round-inline-form">
      <p className="health-field-label" style={{ marginBottom: '0.4rem' }}>Update location</p>
      {loading && <p className="health-loading" style={{ padding: '0.75rem 0' }}>Loading locations…</p>}
      {error && <p className="health-save-error">{error}</p>}
      {!loading && !error && (
        <>
          <div className="round-location-grid">
            {locations.map(loc => (
              <button
                key={loc.id}
                type="button"
                className={`round-location-chip ${selected.has(loc.id) ? 'round-location-chip--active' : ''}`}
                onClick={() => toggle(loc.id)}
              >
                {selected.has(loc.id) && <Icon name="check" size={13} />}
                {loc.name}
              </button>
            ))}
          </div>
          {locations.length === 0 && <p className="health-field-empty">No locations set up yet.</p>}
          <div className="round-inline-actions">
            <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => onSubmit([...selected], locations)}>Save Location</button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Plant info sheet ────────────────────────────────────────────────────────
// Lightweight slide-up sheet shown when tapping the hero in a round card.
// Keeps the user in the round — no navigation. Full profile link warns first.

function PlantSheet({ plantId, displayName, specimenId, onClose, onOpenFull, statusByKey }) {
  const [plant,   setPlant]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [confirmingOpen, setConfirmingOpen] = useState(false);

  useEffect(() => {
    fetch(`/plants/${plantId}`)
      .then(r => { if (!r.ok) throw new Error('Failed to load plant'); return r.json(); })
      .then(data => { setPlant(data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [plantId]);

  // Close on backdrop tap
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const lastObs = plant?.observations?.[0]?.date ?? null;

  return createPortal(
    <div className="round-sheet-backdrop" onClick={handleBackdropClick}>
      <div className="round-sheet">
        {/* Handle + close */}
        <div className="round-sheet-header">
          <div className="round-sheet-handle" />
          <button type="button" className="round-sheet-close" onClick={onClose} aria-label="Close" title="Close">
            <Icon name="x" size={18} />
          </button>
        </div>

        {loading && <p className="health-loading" style={{ padding: '2rem 1rem' }}>Loading…</p>}
        {error   && <p className="health-save-error" style={{ margin: '0 1rem' }}>{error}</p>}

        {plant && !loading && (
          <>
            {/* Hero photo */}
            {plant.cover_photo_id && (
              <div className="round-sheet-hero">
                <img src={photoUrl(plant.cover_photo_id, plant.cover_photo_rotation_version, plant.cover_photo_created_at)} alt={displayName} className="round-sheet-hero-img" />
              </div>
            )}

            <div className="round-sheet-body">
              {/* Name + ID */}
              <div className="round-sheet-name-row">
                <h2 className="round-sheet-name">{displayName}</h2>
                <span className="specimen-id">{specimenId}</span>
              </div>

              {/* Status */}
              {plant.status && (
                <span className={`status-badge status-${plant.status}`} style={{ marginBottom: '0.75rem', display: 'inline-block', ...statusStyle(plant.status, statusByKey) }}>
                  {formatPlantStatus(plant.status, statusByKey)}
                </span>
              )}

              {/* Key fields */}
              <div className="round-sheet-fields">
                {plant.locations?.length > 0 && (
                  <div className="round-sheet-field">
                    <span className="round-sheet-field-label"><Icon name="mapPin" size={12} /> Location</span>
                    <span className="round-sheet-field-value">{plant.locations.map(l => l.name).join(', ')}</span>
                  </div>
                )}
                {plant.growing_method && (
                  <div className="round-sheet-field">
                    <span className="round-sheet-field-label"><Icon name="stone" size={12} /> Growing method</span>
                    <span className="round-sheet-field-value">{plant.growing_method}</span>
                  </div>
                )}
                {plant.substrate && (
                  <div className="round-sheet-field">
                    <span className="round-sheet-field-label">Substrate</span>
                    <span className="round-sheet-field-value">{plant.substrate}</span>
                  </div>
                )}
                {lastObs && (
                  <div className="round-sheet-field">
                    <span className="round-sheet-field-label"><Icon name="clock" size={12} /> Last observation</span>
                    <span className="round-sheet-field-value">{lastObs}</span>
                  </div>
                )}
                {plant.notes && (
                  <div className="round-sheet-field round-sheet-field--notes">
                    <span className="round-sheet-field-label">Notes</span>
                    <span className="round-sheet-field-value">{plant.notes}</span>
                  </div>
                )}
              </div>

              {/* Full profile link */}
              {confirmingOpen ? (
                <div className="inline-confirm" style={{ margin: '0.5rem 0' }}>
                  <span className="inline-confirm-msg">Opening the full profile will exit your Care Round. Your check-ins are saved.</span>
                  <div className="inline-confirm-actions">
                    <button type="button" className="inline-confirm-no" onClick={() => setConfirmingOpen(false)}>Cancel</button>
                    <button type="button" className="inline-confirm-yes inline-confirm-yes--neutral" onClick={onOpenFull}>Open</button>
                  </div>
                </div>
              ) : (
                <button type="button" className="btn btn-ghost round-sheet-full-btn" onClick={() => setConfirmingOpen(true)}>
                  Open full profile <Icon name="chevronRight" size={14} />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

// ─── Round card ──────────────────────────────────────────────────────────────

function RoundCard({ plant, onComplete, onSkip, onPlantUpdated, statusByKey, showCardHint = false, onCardHintDismiss }) {
  const navigate = useNavigate();
  const [activeForm, setActiveForm] = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState(null);
  const [notice,     setNotice]     = useState(null);
  const [showSheet,  setShowSheet]  = useState(false);
  const today = new Date().toISOString().split('T')[0];

  const openForm = (form) => {
    setError(null);
    setNotice(null);
    setActiveForm(form);
  };

  const postObservation = (eventType, notes, detail = {}) =>
    fetch('/observations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ plant_id: plant.plant_id, date: today, event_type: eventType, notes: notes || null, ...detail }),
    }).then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Save failed'); }); return r.json(); });

  const postMeasurement = ({ height, leaf, count, notes }) =>
    fetch('/measurements', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plant_id: plant.plant_id, date: today,
        height_inches:       height ? parseFloat(height) : null,
        longest_leaf_inches: leaf   ? parseFloat(leaf)   : null,
        leaf_count:          count  ? parseInt(count)    : null,
        notes: notes || null,
      }),
    }).then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Save failed'); }); return r.json(); });

  const handleLooksGood = (notes) => {
    setSaving(true); setError(null);
    postObservation('health_check', notes || 'Health check')
      .then(() => { suppressPlant(plant.plant_id); onComplete('looksGood'); })
      .catch(err => { setError(err.message); setSaving(false); });
  };

  const handleObservation = (types, notes, details = {}) => {
    setSaving(true); setError(null);
    // Post sequentially — stop and report on first failure
    types.reduce((chain, type) =>
      chain.then(() => postObservation(type, notes, details[type] ? { [type]: details[type] } : {})),
      Promise.resolve()
    )
      .then(() => { suppressPlant(plant.plant_id); onComplete('observation'); })
      .catch(err => { setError(err.message); setSaving(false); });
  };

  const handleMeasurement = (values) => {
    setSaving(true); setError(null);
    postMeasurement(values)
      .then(() => { suppressPlant(plant.plant_id); onComplete('measurement'); })
      .catch(err => { setError(err.message); setSaving(false); });
  };

  const handleSchedule = (values) => {
    setSaving(true); setError(null);
    fetch('/api/plan/schedules', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(values),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Save failed'); }); return r.json(); })
      .then(() => { setSaving(false); setActiveForm(null); setNotice('Care task scheduled.'); })
      .catch(err => { setError(err.message); setSaving(false); });
  };

  const handleLocationUpdate = (locationIds, allLocations) => {
    setSaving(true); setError(null);
    fetch(`/plants/${plant.plant_id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location_ids: locationIds }),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Save failed'); }); return r.json(); })
      .then(() => {
        const names = allLocations.filter(loc => locationIds.includes(loc.id)).map(loc => loc.name).sort();
        onPlantUpdated(plant.plant_id, { locations: names });
        setSaving(false);
        setActiveForm(null);
        setNotice(names.length ? `Location updated to ${names.join(', ')}.` : 'Location cleared.');
      })
      .catch(err => { setError(err.message); setSaving(false); });
  };

  const dayLabel = plant.days_since === 0 ? 'Checked today'
    : plant.days_since === 1 ? '1 day ago'
    : `${plant.days_since} days ago`;

  const urgencyClass = plant.days_since >= 14 ? 'round-days--urgent'
    : plant.days_since >= 7 ? 'round-days--overdue' : '';

  return (
    <div className="health-card round-card">
      {/* Hero — tap to open plant sheet */}
      <button type="button" className="health-hero round-hero-link" onClick={() => setShowSheet(true)} aria-label={`View ${plant.display_name} details`}>
        {plant.cover_photo_id
          ? <img src={photoUrl(plant.cover_photo_id, plant.cover_photo_rotation_version, plant.cover_photo_created_at)} alt={plant.display_name} className="health-hero-img" style={{ objectPosition: `${plant.cover_photo_offset_x ?? 50}% ${plant.cover_photo_offset_y ?? 50}%`, transform: `scale(${plant.cover_photo_zoom ?? 1})`, transformOrigin: `${plant.cover_photo_offset_x ?? 50}% ${plant.cover_photo_offset_y ?? 50}%` }} />
          : <div className="health-hero-empty"><Icon name="sprout" size={36} /></div>
        }
        <div className="health-hero-overlay">
          <p className="health-hero-name">{plant.display_name} <Icon name="chevronRight" size={13} style={{ opacity: 0.7, marginLeft: 2 }} /></p>
          <div className="health-hero-meta">
            <span className="specimen-id">{plant.specimen_id}</span>
            {plant.locations?.[0] && (
              <span className="round-meta-chip"><Icon name="mapPin" size={11} /> {plant.locations[0]}</span>
            )}
            {plant.growing_method && (
              <span className="round-meta-chip">{plant.growing_method}</span>
            )}
            {plant.status && plant.status !== 'collection' && (
              <span className="round-meta-chip">{formatPlantStatus(plant.status, statusByKey)}</span>
            )}
          </div>
        </div>
      </button>

      {/* Days since + tags */}
      <div className="round-days-row">
        <span className={`round-days ${urgencyClass}`}>
          <Icon name="clock" size={13} /> {dayLabel}
        </span>
        {plant.tags?.map(t => (
          <span key={t.id} className="round-tag-chip">{t.name}</span>
        ))}
      </div>

      {error && <p className="health-save-error" style={{ margin: '0 1rem 0.5rem' }}>{error}</p>}
      {notice && <p className="round-save-notice">{notice}</p>}

      {/* Inline forms */}
      {activeForm === 'looksGood' && (
        <div style={{ padding: '0 1rem' }}>
          <LooksGoodForm plantId={plant.plant_id} onSubmit={handleLooksGood} onCancel={() => setActiveForm(null)} />
        </div>
      )}
      {activeForm === 'observation' && (
        <div style={{ padding: '0 1rem' }}>
          <ObservationForm plantId={plant.plant_id} plantGrowingMethod={plant.growing_method} plantSubstrate={plant.substrate} onSubmit={handleObservation} onCancel={() => setActiveForm(null)} />
        </div>
      )}
      {activeForm === 'measurement' && (
        <div style={{ padding: '0 1rem' }}>
          <MeasurementForm plantId={plant.plant_id} onSubmit={handleMeasurement} onCancel={() => setActiveForm(null)} />
        </div>
      )}
      {activeForm === 'schedule' && (
        <div style={{ padding: '0 1rem' }}>
          <ScheduleCareForm plantId={plant.plant_id} onSubmit={handleSchedule} onCancel={() => setActiveForm(null)} />
        </div>
      )}
      {activeForm === 'location' && (
        <div style={{ padding: '0 1rem' }}>
          <LocationUpdateForm plantId={plant.plant_id} onSubmit={handleLocationUpdate} onCancel={() => setActiveForm(null)} />
        </div>
      )}

      {/* First-card hint */}
      {showCardHint && !activeForm && !saving && (
        <p className="round-card-hint">
          Looks Good logs a health check. Use the buttons below to add an observation, schedule care for later, or skip.
        </p>
      )}

      {/* Actions */}
      {!activeForm && !saving && (
        <div className="round-actions" onClick={onCardHintDismiss}>
          <button type="button" className="btn btn-primary round-action-btn" onClick={() => openForm('looksGood')}>
            <Icon name="check" size={16} /> Looks Good
          </button>
          <button type="button" className="btn btn-secondary round-action-btn" onClick={() => openForm('observation')}>
            <Icon name="plus" size={15} /> Observation
          </button>
          <button type="button" className="btn btn-secondary round-action-btn" onClick={() => openForm('measurement')}>
            <Icon name="ruler" size={15} /> Measurement
          </button>
          <button type="button" className="btn btn-secondary round-action-btn" onClick={() => openForm('schedule')}>
            <Icon name="calendar" size={15} /> Schedule Care
          </button>
          <button type="button" className="btn btn-secondary round-action-btn" onClick={() => openForm('location')}>
            <Icon name="mapPin" size={15} /> Location
          </button>
          <button type="button" className="btn btn-ghost round-action-btn round-skip-btn" onClick={onSkip}>
            Skip
          </button>
        </div>
      )}

      {saving && <p className="health-saving" style={{ padding: '1rem' }}>Saving…</p>}

      {/* Plant info sheet */}
      {showSheet && (
        <PlantSheet
          plantId={plant.plant_id}
          displayName={plant.display_name}
          specimenId={plant.specimen_id}
          onClose={() => setShowSheet(false)}
          onOpenFull={() => navigate(`/plants/${plant.plant_id}`, { state: { returnTo: '/rounds' } })}
          statusByKey={statusByKey}
        />
      )}
    </div>
  );
}

// ─── Propagation item card (Propagation Tray round) ─────────────────────────

const PROP_PIPELINE = [
  { key: 'active',    label: 'Active' },
  { key: 'sprouting', label: 'Sprouting', hint: 'First signs of life' },
  { key: 'rooting',   label: 'Rooting',   hint: 'Roots emerging or confirmed' },
  { key: 'rooted',    label: 'Rooted',    hint: 'Established roots, ready to pot' },
  { key: 'plant',     label: 'Plant',     hint: 'Graduated' },
];

function PropStatusPipeline({ currentStatus }) {
  const currentIdx = PROP_PIPELINE.findIndex(s => s.key === currentStatus);
  return (
    <div className="round-prop-pipeline">
      {PROP_PIPELINE.map((stage, i) => {
        const isDone    = i < currentIdx;
        const isCurrent = i === currentIdx;
        const isNext    = i === currentIdx + 1;
        return (
          <div key={stage.key} className="round-prop-pipeline-step">
            <div className="round-prop-pipeline-node">
              <div
                title={stage.hint || ''}
                className={`round-prop-pipeline-dot${isCurrent ? ' round-prop-pipeline-dot--current' : isDone ? ' round-prop-pipeline-dot--filled' : ''}`}
              />
              <span className={`round-prop-pipeline-label${isCurrent ? ' round-prop-pipeline-label--current' : isNext ? ' round-prop-pipeline-label--next' : ''}`}>
                {stage.label}
              </span>
            </div>
            {i < PROP_PIPELINE.length - 1 && (
              <div className={`round-prop-pipeline-line${isDone ? ' round-prop-pipeline-line--done' : ''}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function PropItemCard({ item, onComplete, onSkip }) {
  const fieldOptions   = useFieldOptions();
  const lossReasonOpts = fieldOptionList(fieldOptions, 'loss_reason');

  const [activeForm,   setActiveForm]   = useState(null);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState(null);
  const [graduateName, setGraduateName] = useState('');
  const [rootLength,   setRootLength]   = useState(item.root_length_mm ? String(item.root_length_mm) : '');
  const [failReason,   setFailReason]   = useState('');
  const [localStatus,  setLocalStatus]  = useState(item.status);

  // Keep localStatus in sync if Rounds swaps a new item into the same card slot
  useEffect(() => { setLocalStatus(item.status); }, [item.id, item.status]);

  const today = new Date().toISOString().split('T')[0];

  const patchItem = (updates, actionType) => {
    setSaving(true); setError(null);
    fetch(`/propagation-items/${item.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Save failed'); }); return r.json(); })
      .then(() => { setSaving(false); onComplete(actionType); })
      .catch(err => { setError(err.message); setSaving(false); });
  };

  const handleCheckIn = () => patchItem({ last_checked_at: today }, 'looksGood');
  const handleAdvance = () => {
    const next = PROP_STATUS_ORDER[PROP_STATUS_ORDER.indexOf(item.status) + 1];
    if (!next) return;
    setSaving(true); setError(null);
    fetch(`/propagation-items/${item.id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: next, last_checked_at: today }),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Save failed'); }); return r.json(); })
      .then(() => {
        setSaving(false);
        setLocalStatus(next);
        if (next === 'rooted') {
          // Stay on card and prompt for graduation instead of advancing the deck
          setActiveForm('graduate');
        } else {
          onComplete('observation');
        }
      })
      .catch(err => { setError(err.message); setSaving(false); });
  };
  const handleFail = () => patchItem({ status: 'failed', failure_reason: failReason || null }, 'observation');

  const handleGraduate = () => {
    setSaving(true); setError(null);
    fetch(`/propagation-items/${item.id}/graduate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        plant_name: graduateName.trim() || null,
        root_length_mm: rootLength ? parseInt(rootLength, 10) : null,
      }),
    })
      .then(r => { if (!r.ok) return r.json().then(e => { throw new Error(e.detail || 'Save failed'); }); return r.json(); })
      .then(() => { setSaving(false); onComplete('observation'); })
      .catch(err => { setError(err.message); setSaving(false); });
  };

  const nextStatus = PROP_STATUS_ORDER[PROP_STATUS_ORDER.indexOf(localStatus) + 1];
  const daysSince = item.last_checked_at
    ? Math.floor((Date.now() - new Date(item.last_checked_at).getTime()) / 86400000)
    : null;
  const dayLabel = daysSince === null ? 'never checked'
    : daysSince === 0 ? 'checked today'
    : `${daysSince}d since check`;
  const urgencyClass = daysSince === null || daysSince > 7 ? 'round-days--urgent'
    : daysSince > 3 ? 'round-days--overdue' : '';

  const heroOffsetX = item.parent_cover_photo_offset_x ?? 50;
  const heroOffsetY = item.parent_cover_photo_offset_y ?? 50;

  return (
    <div className="health-card round-card">
      {/* Parent plant hero photo (when available) */}
      {item.parent_cover_photo_id && (
        <Link to={`/plants/${item.parent_plant_id}`} state={{ returnTo: '/rounds' }} className="round-prop-hero-link">
          <div className="round-prop-hero">
            <img
              src={photoUrl(item.parent_cover_photo_id, item.parent_cover_photo_rotation_version, item.parent_cover_photo_created_at)}
              alt={item.parent_display_name || ''}
              className="round-prop-hero-img"
              style={{ objectPosition: `${heroOffsetX}% ${heroOffsetY}%` }}
            />
            <div className="round-prop-hero-overlay">
              <span className="round-prop-hero-name">
                {item.parent_display_name || item.parent_specimen_id}
              </span>
              {item.parent_display_name && item.parent_specimen_id && (
                <span className="round-prop-hero-id">
                  {item.parent_specimen_id}
                </span>
              )}
            </div>
          </div>
        </Link>
      )}

      {/* Header — show text name row only when there is no hero photo */}
      <div className="round-prop-header">
        {!item.parent_cover_photo_id && (
          <div className="round-prop-name-row">
            <Icon name="leaf" size={14} className="round-prop-name-icon" />
            <span className="round-prop-name">
              {item.parent_display_name || item.parent_specimen_id || 'Unknown source'}
            </span>
            {item.parent_specimen_id && item.parent_display_name && (
              <span className="specimen-id">{item.parent_specimen_id}</span>
            )}
          </div>
        )}
        <div className="round-prop-detail-row">
          <span className="round-prop-label">
            {item.label || `Item ${item.id}`}
          </span>
          <span className={`round-prop-status round-prop-status--${localStatus}`}>
            {PROP_STATUS_LABELS[localStatus] || localStatus}
          </span>
          {item.propagation_method && (
            <span className="round-prop-method">
              {PROP_METHOD_LABELS[item.propagation_method] || item.propagation_method}
            </span>
          )}
        </div>
      </div>

      {/* Status pipeline */}
      <PropStatusPipeline currentStatus={localStatus} />

      {/* Meta row */}
      <div className="round-days-row" style={{ padding: '0.25rem 1rem 0.4rem' }}>
        <span className={`round-days ${urgencyClass}`}>
          <Icon name="clock" size={13} /> {dayLabel}
        </span>
        {item.prop_substrate && <span className="round-meta-chip">{formatSubstrate(item.prop_substrate)}</span>}
        {item.prop_enclosure && <span className="round-meta-chip">{item.prop_enclosure}</span>}
        {item.location_name && <span className="round-meta-chip"><Icon name="mapPin" size={11} /> {item.location_name}</span>}
      </div>

      {error && <p className="health-save-error" style={{ margin: '0 1rem 0.5rem' }}>{error}</p>}

      {/* Graduate inline form */}
      {activeForm === 'graduate' && (
        <div style={{ padding: '0 1rem 0.75rem' }}>
          <div className="round-inline-form">
            <div className="round-form-grid" style={{ marginBottom: '0.5rem' }}>
              <label className="round-form-field">
                <span className="health-field-label">Common name (optional)</span>
                <input type="text" value={graduateName} onChange={e => setGraduateName(e.target.value)}
                  className="round-measure-input" placeholder="Leave blank for species name" />
              </label>
              <label className="round-form-field">
                <span className="health-field-label">Root length mm (optional)</span>
                <input type="number" value={rootLength} onChange={e => setRootLength(e.target.value)}
                  className="round-measure-input" placeholder="e.g. 25" />
              </label>
            </div>
            <div className="round-inline-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => onComplete('observation')} disabled={saving}>Later</button>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleGraduate} disabled={saving}>
                {saving ? 'Creating…' : 'Graduate →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fail confirm form */}
      {activeForm === 'fail' && (
        <div style={{ padding: '0 1rem 0.75rem' }}>
          <div className="round-inline-form">
            <label className="round-form-field">
              <span className="health-field-label">Failure reason (optional)</span>
              <select value={failReason} onChange={e => setFailReason(e.target.value)} className="round-measure-input">
                <option value="">— Unknown —</option>
                {lossReasonOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
            <div className="round-inline-actions" style={{ marginTop: '0.5rem' }}>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => setActiveForm(null)} disabled={saving}>Cancel</button>
              <button type="button" className="btn btn-sm btn-danger" onClick={handleFail} disabled={saving}>
                {saving ? 'Saving…' : 'Confirm failed'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      {!activeForm && !saving && (
        <div className="round-actions">
          <button type="button" className="btn btn-primary round-action-btn" onClick={handleCheckIn}>
            <Icon name="check" size={16} /> Looks good
          </button>
          {nextStatus && (
            <button type="button" className="btn btn-secondary round-action-btn" onClick={handleAdvance}>
              <Icon name="arrowUp" size={15} /> Advance → {PROP_STATUS_LABELS[nextStatus]}
            </button>
          )}
          {localStatus === 'rooted' && (
            <button type="button" className="btn btn-secondary round-action-btn" onClick={() => setActiveForm('graduate')}>
              <Icon name="sprout" size={15} /> Graduate to plant
            </button>
          )}
          <button type="button" className="btn btn-secondary round-action-btn round-action-btn--danger" onClick={() => setActiveForm('fail')}>
            Mark as failed
          </button>
          <button type="button" className="btn btn-ghost round-action-btn round-skip-btn" onClick={onSkip}>Skip</button>
        </div>
      )}
      {saving && <p className="health-saving" style={{ padding: '1rem' }}>Saving…</p>}
    </div>
  );
}

// ─── Sub-filter picker ───────────────────────────────────────────────────────

function SubFilterPicker({ theme, queue, onStart, onBack, statusByKey }) {
  const [selected, setSelected] = useState(new Set());

  // Derive available options from the queue
  const options = (() => {
    if (theme === 'location') {
      const seen = new Set();
      const opts = [];
      queue.forEach(p => p.locations.forEach(l => { if (!seen.has(l)) { seen.add(l); opts.push(l); } }));
      return opts.sort();
    }
    if (theme === 'status') {
      const preferred = ['propagation', 'available', 'collection', 'loaned_in', 'loaned_out'];
      const seen = new Set(queue.map(p => p.status).filter(Boolean));
      return preferred.filter(status => seen.has(status)).concat([...seen].filter(status => !preferred.includes(status)).sort());
    }
    if (theme === 'growing_method') {
      const seen = new Set();
      queue.forEach(p => { if (p.growing_method && !seen.has(p.growing_method)) seen.add(p.growing_method); });
      return [...seen].sort();
    }
    if (theme === 'tag') {
      const seen = new Set();
      // Only plants that have tags (no-tag plants excluded from tag rounds)
      queue.forEach(p => p.tags.forEach(t => { if (!seen.has(t.name)) seen.add(t.name); }));
      return [...seen].sort();
    }
    return [];
  })();

  const toggle = (val) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(val) ? next.delete(val) : next.add(val);
      return next;
    });
  };

  const selectAll = () => setSelected(new Set(options));
  const allSelected = selected.size === options.length && options.length > 0;

  const filterParam = theme === 'location' ? 'filter_locations'
    : theme === 'growing_method' ? 'filter_methods'
    : theme === 'status' ? 'filter_statuses'
    : 'filter_tags';

  const optionLabel = (opt) => theme === 'status' ? formatPlantStatus(opt, statusByKey) : opt;
  const themeLabel = theme === 'location' ? 'locations'
    : theme === 'growing_method' ? 'growing methods'
    : theme === 'status' ? 'statuses'
    : 'tags';

  return (
    <div className="round-theme-picker" style={{ animation: 'healthFadeIn 0.2s ease-out' }}>
      <p className="round-theme-intro">
        Select which {themeLabel} to include.
        <span className="round-subfilter-hint"> Tap to select multiple.</span>
      </p>

      {options.length === 0 ? (
        <p className="round-subfilter-empty">No options found in your current collection.</p>
      ) : (
        <>
          <div className="round-subfilter-chips">
            {options.map(opt => (
              <button
                type="button"
                key={opt}
                className={`round-subfilter-chip ${selected.has(opt) ? 'round-subfilter-chip--active' : ''}`}
                onClick={() => toggle(opt)}
              >
                {selected.has(opt) && <Icon name="check" size={13} />}
                {optionLabel(opt)}
              </button>
            ))}
          </div>

          <button
            type="button"
            className="round-select-all"
            onClick={allSelected ? () => setSelected(new Set()) : selectAll}
          >
            {allSelected ? 'Deselect all' : 'Select all'}
          </button>

          <div className="round-subfilter-actions">
            <button type="button" className="btn btn-ghost" onClick={onBack}>← Back</button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={selected.size === 0}
              onClick={() => {
                const values = [...selected].join(',');
                onStart({ [filterParam]: values });
              }}
            >
              Start Round ({selected.size} selected) →
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Theme picker ────────────────────────────────────────────────────────────

function ThemePicker({ onSelect, activePropCount = 0, showFirstUseIntro = false }) {
  const visibleOptions = THEME_OPTIONS.filter(
    opt => opt.key !== 'propagation_tray' || activePropCount > 0
  );
  return (
    <div className="round-theme-picker">
      {showFirstUseIntro ? (
        <p className="round-theme-intro">
          Pick a theme to build your queue. You'll review plants one at a time and log observations, care events, or scheduled tasks per card.
        </p>
      ) : (
        <p className="round-theme-intro">How would you like to check on your plants today?</p>
      )}
      <div className="round-theme-list">
        {visibleOptions.map(opt => (
          <button type="button" key={opt.key} className="round-theme-option" onClick={() => onSelect(opt.key)}>
            <span className="round-theme-icon"><Icon name={opt.icon} size={20} /></span>
            <span className="round-theme-text">
              <span className="round-theme-label">{opt.label}</span>
              <span className="round-theme-desc">{opt.description}</span>
            </span>
            {opt.key === 'propagation_tray' && activePropCount > 0 && (
              <span className="round-theme-prop-badge">{activePropCount}</span>
            )}
            <Icon name="chevronRight" size={16} className="round-theme-arrow" />
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Batch pause screen ──────────────────────────────────────────────────────

function BatchPause({ batchActions, totalCompleted, remaining, onContinue, onDone, noun = 'plant' }) {
  const counts = batchActions.reduce((acc, a) => { acc[a] = (acc[a] || 0) + 1; return acc; }, {});
  return (
    <div className="health-pause">
      <div className="health-pause-icon"><Icon name="check" size={28} /></div>
      <h2 className="health-pause-title">
        {remaining === 0 ? 'Round complete.' : 'Batch complete.'}
      </h2>
      <div className="round-pause-tally">
        {Object.entries(counts).map(([action, n]) => (
          <span key={action} className="round-pause-tally-item" style={{ color: ACTION_COLORS[action]?.bg === 'var(--border)' ? 'var(--text-muted)' : ACTION_COLORS[action]?.bg }}>
            {n} {ACTION_COLORS[action]?.label}
          </span>
        ))}
      </div>
      <p className="health-pause-body">
        {remaining > 0 ? `${remaining} ${noun}${remaining !== 1 ? 's' : ''} still in queue.` : `All ${noun}s checked!`}
      </p>
      {remaining > 0 && (
        <button type="button" className="btn btn-primary health-pause-btn" onClick={onContinue}>
          Keep going ({remaining} remaining) →
        </button>
      )}
      <button type="button" className="btn btn-ghost health-pause-btn" onClick={onDone}>
        {remaining === 0 ? '← Done' : 'Done for now'}
      </button>
    </div>
  );
}

// ─── Rounds screen ────────────────────────────────────────────────────────────

export default function Rounds() {
  const navigate = useNavigate();
  const { byKey: statusByKey } = useStatusMeta();
  const { hasSeen, markSeen } = useGuidance();
  const [showRoundsHelp, setShowRoundsHelp] = useState(false);

  // Step: 'theme' | 'subfilter' | 'queue'
  const [step,         setStep]         = useState('theme');
  const [theme,        setTheme]        = useState(null);

  // Full queue fetched once — filtered client-side for suppressed plants
  const [fullQueue, setFullQueue] = useState([]);
  const [queue,     setQueue]     = useState([]);
  const [cardIdx,   setCardIdx]   = useState(0);

  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const connectivity = useConnectivity();

  // Queue mode: 'plants' for normal rounds, 'items' for Propagation Tray
  const [queueMode, setQueueMode] = useState('plants');
  const [activePropCount, setActivePropCount] = useState(0);
  // null = loading, 0 = empty collection, >0 = has plants
  const [plantCount, setPlantCount] = useState(null);

  useEffect(() => {
    fetch('/propagation-items/count')
      .then(r => r.ok ? r.json() : { active: 0 })
      .then(d => setActivePropCount(d.active || 0))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/plants')
      .then(r => r.ok ? r.json() : [])
      .then(data => setPlantCount(data.length))
      .catch(() => setPlantCount(0));
  }, []);

  // Batch tracking
  const [batchStart,    setBatchStart]    = useState(0);
  const [batchActions,  setBatchActions]  = useState([]); // actions in current batch
  const [totalComplete, setTotalComplete] = useState(0);
  const [showPause,     setShowPause]     = useState(false);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  // On mount: detect ?stale=true and immediately start round with overdue plants
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('stale') !== 'true') return;
    setLoading(true);
    setError(null);
    fetch('/plants/rounds?stale=true')
      .then(r => { if (!r.ok) throw new Error('Failed to load plants'); return r.json(); })
      .then(data => {
        const filtered = data.filter(p => !isSuppressed(p.plant_id));
        setQueue(filtered);
        setCardIdx(0);
        setBatchStart(0);
        setBatchActions([]);
        setTotalComplete(0);
        setShowPause(false);
        setLoading(false);
        setStep('queue');
      })
      .catch(err => { setError(err.message); setLoading(false); });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch queue when theme is selected.
  // For themes with a sub-filter: stores data in fullQueue for SubFilterPicker to derive options.
  // For themes without a sub-filter: applies queue directly in the fetch callback to avoid
  // stale closure issues (fullQueue wouldn't be readable in a useEffect after setFullQueue).
  useEffect(() => {
    if (!theme) return;
    setLoading(true);
    setError(null);

    if (theme === 'propagation_tray') {
      fetch('/propagation-items/rounds')
        .then(r => { if (!r.ok) throw new Error('Failed to load propagation items'); return r.json(); })
        .then(data => {
          setQueue(data.items || []);
          setQueueMode('items');
          setCardIdx(0);
          setBatchStart(0);
          setBatchActions([]);
          setTotalComplete(0);
          setShowPause(false);
          setLoading(false);
          setStep('queue');
        })
        .catch(err => { setError(err.message); setLoading(false); });
      return;
    }

    setQueueMode('plants');
    const opt = THEME_OPTIONS.find(t => t.key === theme);
    fetch(`/plants/rounds?group_by=${theme}`)
      .then(r => { if (!r.ok) throw new Error('Failed to load plants'); return r.json(); })
      .then(data => {
        if (opt?.hasSubFilter) {
          // Sub-filter themes: store for picker, stay on subfilter step
          setFullQueue(data);
          setLoading(false);
        } else {
          // No sub-filter: apply directly, skip subfilter step entirely
          const filtered = data.filter(p => !isSuppressed(p.plant_id));
          setQueue(filtered);
          setCardIdx(0);
          setBatchStart(0);
          setBatchActions([]);
          setTotalComplete(0);
          setShowPause(false);
          setLoading(false);
          setStep('queue');
        }
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [theme]);

  // Build filtered queue when sub-filter selections are confirmed
  const startQueue = (params) => {
    const qs = new URLSearchParams({ group_by: theme, ...params }).toString();
    setLoading(true);
    fetch(`/plants/rounds?${qs}`)
      .then(r => { if (!r.ok) throw new Error('Failed to load plants'); return r.json(); })
      .then(data => {
        const filtered = data.filter(p => !isSuppressed(p.plant_id));
        setQueue(filtered);
        setCardIdx(0);
        setBatchStart(0);
        setBatchActions([]);
        setTotalComplete(0);
        setShowPause(false);
        setLoading(false);
        setStep('queue');
      })
      .catch(err => { setError(err.message); setLoading(false); });
  };

  const handleThemeSelect = (key) => {
    if (!hasSeen('seen_rounds_intro')) markSeen('seen_rounds_intro');
    setTheme(key);
    const opt = THEME_OPTIONS.find(t => t.key === key);
    // Sub-filter themes show the picker; no-sub-filter themes go to loading state
    // and the fetch useEffect above handles the transition to queue step directly
    setStep(opt?.hasSubFilter ? 'subfilter' : 'subfilter_loading');
  };

  const currentBatch = queue.slice(batchStart, batchStart + BATCH_SIZE);
  const batchCardIdx = cardIdx - batchStart;
  const currentPlant = currentBatch[batchCardIdx];
  const remaining    = queue.length - cardIdx - 1;

  const advance = (actionType) => {
    const newActions = [...batchActions, actionType];
    setBatchActions(newActions);
    if (actionType !== 'skip') setTotalComplete(n => n + 1);

    const nextIdx = cardIdx + 1;

    // End of batch
    if (newActions.length >= BATCH_SIZE || nextIdx >= queue.length) {
      setShowPause(true);
      setCardIdx(nextIdx);
      return;
    }

    setCardIdx(nextIdx);
    window.scrollTo(0, 0);
  };

  const handleComplete = (actionType) => advance(actionType);

  const handleSkip = () => {
    // Move current plant to end of queue
    const skipped = queue[cardIdx];
    const newQueue = [...queue.slice(0, cardIdx), ...queue.slice(cardIdx + 1), skipped];
    setQueue(newQueue);
    advance('skip');
  };

  const handlePlantUpdated = (plantId, updates) => {
    const applyUpdates = p => p.plant_id === plantId ? { ...p, ...updates } : p;
    setQueue(prev => prev.map(applyUpdates));
    setFullQueue(prev => prev.map(applyUpdates));
  };

  const handleContinue = () => {
    setBatchStart(b => b + BATCH_SIZE);
    setBatchActions([]);
    setShowPause(false);
    window.scrollTo(0, 0);
  };

  const handleDone = () => navigate('/', { replace: true });

  const handleBack = () => {
    if (step === 'subfilter') {
      setStep('theme'); setTheme(null); setFullQueue([]);
    } else if (step === 'queue') {
      const inProgress = batchActions.length > 0 || totalComplete > 0;
      if (inProgress) {
        setConfirmingLeave(true);
      } else {
        navigate('/', { replace: true });
      }
    } else {
      setStep('theme'); setTheme(null); setFullQueue([]);
    }
  };

  const allDone = step === 'queue' && !loading && queue.length > 0 && cardIdx >= queue.length;

  return (
    <div className="app">
      <header className="sub-header">
        <button
          type="button"
          className="sub-header-back"
          onClick={step === 'theme' ? () => navigate('/', { replace: true }) : handleBack}
          aria-label={step === 'theme' ? 'Back' : 'Exit round'}
          title={step === 'theme' ? 'Back' : 'Exit round'}
        >
          <Icon name={step === 'queue' && (batchActions.length > 0 || totalComplete > 0) ? 'x' : 'arrowLeft'} size={20} />
        </button>
        <div className="sub-header-title">
          <h1>Care Rounds</h1>
        </div>
        <div className="sub-header-actions">
          <button
            type="button"
            className="sub-header-action"
            onClick={() => setShowRoundsHelp(true)}
            aria-label="How Care Rounds works"
            title="How Care Rounds works"
          >
            <Icon name="helpCircle" size={18} />
          </button>
        </div>
      </header>
      <GuidanceSheet
        variant="sheet"
        isOpen={showRoundsHelp}
        onDismiss={() => setShowRoundsHelp(false)}
        title={ROUNDS_GUIDANCE_TITLE}
        primaryAction={{ label: 'Got it', onClick: () => setShowRoundsHelp(false) }}
      >
        <RoundsGuidanceBody />
      </GuidanceSheet>

      {/* Connectivity check — or fetch error — replaces all step content */}
      {(connectivity === false || error) ? (
        <ErrorScreen
          message={error || 'Load failed'}
          onRetry={() => { setError(null); setStep('theme'); setTheme(null); setFullQueue([]); }}
        />
      ) : connectivity === null ? (
        <p className="health-loading">Checking connection…</p>
      ) : (
        <>
          {/* Empty collection state — shown instead of theme picker */}
          {step === 'theme' && plantCount === 0 && (
            <div className="rounds-no-plants-state">
              <Icon name="waypoints" size={48} className="rounds-no-plants-icon" />
              <p className="rounds-no-plants-title">Care Rounds guides you through your collection, one plant at a time.</p>
              <div className="rounds-no-plants-features">
                <div className="guidance-feature-row">
                  <span className="guidance-feature-icon"><Icon name="clock" size={16} /></span>
                  <span className="guidance-feature-text">
                    <strong>Most Overdue</strong> — start with the plants that need attention most
                  </span>
                </div>
                <div className="guidance-feature-row">
                  <span className="guidance-feature-icon"><Icon name="dice5" size={16} /></span>
                  <span className="guidance-feature-text">
                    <strong>Surprise Me</strong> — a smart shuffle weighted toward neglected plants
                  </span>
                </div>
                <div className="guidance-feature-row">
                  <span className="guidance-feature-icon"><Icon name="mapPin" size={16} /></span>
                  <span className="guidance-feature-text">
                    <strong>By Location, Method, Status, or Tag</strong> — focus on any subset
                  </span>
                </div>
              </div>
              <p className="rounds-no-plants-cta-label">Add a plant to start your first round.</p>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate('/new')}
              >
                Add your first plant →
              </button>
            </div>
          )}

          {/* Theme picker */}
          {step === 'theme' && plantCount !== 0 && (
            <ThemePicker
              onSelect={handleThemeSelect}
              activePropCount={activePropCount}
              showFirstUseIntro={!hasSeen('seen_rounds_intro')}
            />
          )}

          {/* Loading */}
          {(loading || step === 'subfilter_loading') && (
            <p className="health-loading">Building your round…</p>
          )}

          {/* Sub-filter picker */}
          {step === 'subfilter' && !loading && (
            <SubFilterPicker
              theme={theme}
              queue={fullQueue}
              onStart={startQueue}
              onBack={() => { setStep('theme'); setTheme(null); setFullQueue([]); }}
              statusByKey={statusByKey}
            />
          )}

          {/* Leave round confirmation */}
          {confirmingLeave && (
            <div className="inline-confirm" style={{ margin: '0.75rem 1rem 0' }}>
              <span className="inline-confirm-msg">Leave this round? Your check-ins are already saved.</span>
              <div className="inline-confirm-actions">
                <button type="button" className="inline-confirm-no" onClick={() => setConfirmingLeave(false)}>Stay</button>
                <button type="button" className="inline-confirm-yes inline-confirm-yes--neutral" onClick={() => navigate('/', { replace: true })}>Leave</button>
              </div>
            </div>
          )}

          {/* Queue */}
          {step === 'queue' && !loading && (
            <>
              {/* Empty state */}
              {queue.length === 0 && (
                <div className="health-pause">
                  <div className="health-pause-icon"><Icon name="check" size={28} /></div>
                  <h2 className="health-pause-title">All caught up!</h2>
                  <p className="health-pause-body">
                    {queueMode === 'items'
                      ? 'No active propagation items to check in on.'
                      : 'No plants need attention right now. Check back in a few days.'}
                  </p>
                  <button type="button" className="btn btn-ghost health-pause-btn" onClick={() => navigate('/', { replace: true })}>← Done</button>
                </div>
              )}

              {/* All done */}
              {allDone && (
                <BatchPause
                  batchActions={batchActions}
                  totalCompleted={totalComplete}
                  remaining={0}
                  onContinue={handleContinue}
                  onDone={handleDone}
                  noun={queueMode === 'items' ? 'item' : 'plant'}
                />
              )}

              {/* Batch pause */}
              {showPause && !allDone && (
                <BatchPause
                  batchActions={batchActions}
                  totalCompleted={totalComplete}
                  remaining={remaining}
                  onContinue={handleContinue}
                  onDone={handleDone}
                  noun={queueMode === 'items' ? 'item' : 'plant'}
                />
              )}

              {/* Active card */}
              {!showPause && !allDone && currentPlant && (
                <>
                  <CounterBar actions={batchActions} batchOffset={batchStart} queueSize={queue.length} />
                  {queueMode === 'items' ? (
                    <PropItemCard
                      key={`prop-${currentPlant.id}-${cardIdx}`}
                      item={currentPlant}
                      onComplete={handleComplete}
                      onSkip={handleSkip}
                    />
                  ) : (
                    <RoundCard
                      key={`${currentPlant.plant_id}-${cardIdx}`}
                      plant={currentPlant}
                      onComplete={handleComplete}
                      onSkip={handleSkip}
                      onPlantUpdated={handlePlantUpdated}
                      statusByKey={statusByKey}
                      showCardHint={cardIdx === 0 && !hasSeen('seen_rounds_card_hint')}
                      onCardHintDismiss={() => {
                        if (!hasSeen('seen_rounds_card_hint')) markSeen('seen_rounds_card_hint');
                      }}
                    />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
